import { SlideConfig, FormData } from '../types';

/**
 * Mapping da slide ID a benefit config key
 */
export const SLIDE_TO_BENEFIT_KEY: Record<string, string> = {
  name_form: 'name_form',
  email_form: 'email_form',
  phone_form: 'phone_form',
};

/**
 * Configurazione delle slide del wizard SliderPartnership
 * 
 * Versione semplificata:
 * - Prodotto preimpostato: Photovoltaic
 * - Timeline preimpostata: one_to_two_months
 * - 3 vantaggi invece di 4
 * 
 * Slide types:
 * - SINGLE_ANSWER: Selezione singola con griglia di opzioni
 * - FORM_INPUT: Form con input fields
 * - INFO: Slide informativa (loading, success, error)
 */
export const SLIDES_CONFIG: SlideConfig[] = [
  // ============================================
  // SLIDE 1: Tipo edificio
  // ============================================
  {
    id: 'house_type',
    type: 'SINGLE_ANSWER',
    title: 'Per quale tipo di edificio vuoi scoprire i bonus?',
    apiFieldName: 'house_type',
    autoAdvance: true,
    answers: [
      {
        label: 'Casa unifamiliare',
        value: 'detached_house',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a690347c3338223a5c8d5d_IT_Slider_Icons_casa_unifamiliare.svg',
      },
      {
        label: 'Casa a schiera',
        value: 'townhouse',
        image: 'https://uploads-ssl.webflow.com/62da8209945d9e1b7e7daf07/65a69035fd81055cfcf886db_IT_Slider_Icons_casa_a_schiera.svg',
      },
      {
        label: 'Condominio (più di 6 famiglie)',
        value: 'multi_family_house',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/672101f43a7562ecc9146c47_IT_Slider_Icons_Condominio.svg',
      },
      {
        label: 'Palazzina (fino a 6 famiglie)',
        value: 'small_building',
        image: 'https://cdn.prod.website-files.com/6621fcb456e6148dbbc48ea9/672101f39fd017299f37df74_IT_Slider_Icons_Copropriet%C3%A0.svg',
      },
    ],
  },

  // ============================================
  // SLIDE 2: Form CAP con feedback
  // ============================================
  {
    id: 'location_form',
    type: 'FORM_INPUT',
    title: 'Scopri gli incentivi e bonus nella tua zona',
    inputs: [
      {
        name: 'zip',
        placeholder: 'Il tuo CAP',
        type: 'tel',
        required: true,
        pattern: '^\\d{5}$',
        maxLength: 5,
      },
    ],
  },

  // ============================================
  // SLIDE 3: Form Nome (VANTAGGIO 1/3)
  // ============================================
  {
    id: 'name_form',
    type: 'FORM_INPUT',
    title: '',
    header: {
      savingsSubText: 'VANTAGGIO 1/3',
      savingsText: '', // Valore dinamico dal database CAP
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'name',
        placeholder: 'Nome e cognome',
        type: 'text',
        required: true,
      },
    ],
  },

  // ============================================
  // SLIDE 4: Form Email (VANTAGGIO 2/3)
  // ============================================
  {
    id: 'email_form',
    type: 'FORM_INPUT',
    title: '',
    header: {
      savingsSubText: 'VANTAGGIO 2/3',
      savingsText: '', // Valore dinamico dal database CAP
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'email',
        placeholder: 'Il tuo indirizzo email',
        type: 'email',
        required: true,
      },
    ],
  },

  // ============================================
  // SLIDE 5: Form Telefono (VANTAGGIO 3/3)
  // ============================================
  {
    id: 'phone_form',
    type: 'FORM_INPUT',
    title: '',
    header: {
      savingsSubText: 'VANTAGGIO 3/3',
      savingsText: '', // Valore dinamico dal database CAP
      indicator: { label: '' },
    },
    inputs: [
      {
        name: 'phone',
        placeholder: 'Numero di cellulare',
        type: 'tel',
        required: true,
        pattern: '^[\\d\\s]{10,18}$',
      },
    ],
  },

  // ============================================
  // SLIDE 6: Loading
  // ============================================
  {
    id: 'loading',
    type: 'INFO',
    title: 'Invio della richiesta in corso...',
    html: '<p>Attendere prego...</p>',
  },

  // ============================================
  // SLIDE 7: Success
  // ============================================
  {
    id: 'success',
    type: 'INFO',
    title: '',
  },

  // ============================================
  // SLIDE 8: Error
  // ============================================
  {
    id: 'error',
    type: 'INFO',
    title: 'Oops, qualcosa è andato storto',
    html: '<p>Si è verificato un errore...</p>',
  },
];

/**
 * Trova l'indice di una slide per ID
 */
export const getSlideIndexById = (id: string): number => {
  return SLIDES_CONFIG.findIndex((slide) => slide.id === id);
};

/**
 * Slide IDs che mostrano benefici dinamici (hanno header)
 */
export const BENEFIT_SLIDE_IDS = [
  'name_form',
  'email_form',
  'phone_form',
] as const;

/**
 * Slide IDs per il calcolo del progresso
 */
export const PROGRESS_SLIDE_IDS = SLIDES_CONFIG
  .filter((slide) => slide.type === 'SINGLE_ANSWER' || slide.type === 'FORM_INPUT')
  .map((slide) => slide.id);

/**
 * Ottiene la chiave del beneficio per un dato slide ID
 */
export function getBenefitKeyForSlide(slideId: string): string {
  return SLIDE_TO_BENEFIT_KEY[slideId] || slideId;
}

/**
 * Ottiene il nome breve del beneficio per il testo del bottone CTA
 */
export function getBenefitShortName(benefitText: string | null): string {
  if (!benefitText) return 'il prossimo Beneficio';

  const text = benefitText.toLowerCase();

  if (text.includes('bolletta')) return 'il Risparmio in Bolletta';
  if (text.includes('surplus')) return 'il Surplus CER';
  if (text.includes('rivendita') && !text.includes('surplus')) return 'la Rivendita Energia';
  if (text.includes('ecobonus')) return "l'Ecobonus";
  if (text.includes('pnrr') || text.includes('cer enpal')) return 'il PNRR CER Enpal';
  if (text.includes('conto termico')) return 'il Conto Termico';
  if (text.includes('bonus regione fvg')) return 'il Bonus Regione FVG';
  if (text.includes('risparmio totale')) return 'il Risparmio Totale';

  return 'il prossimo Beneficio';
}
